/**
 * Shared transforms for the search API payload.
 * See docs/engineering/search-payload-parity.md for architecture and usage.
 */

// Branded types — only the corresponding transform function can produce these

declare const ExperienceTagBrand: unique symbol;
export type TransformedExperienceTags = string[] & { readonly [ExperienceTagBrand]: true };

declare const ExpandedFeaturesBrand: unique symbol;
export type ExpandedFeatures = string[] & { readonly [ExpandedFeaturesBrand]: true };

// Canonical payload shape — both SSR and client must produce this type

export interface SearchApiPayload {
  instant_bookable: boolean;
  passengers: number;
  durations: string[];
  package_types: string[];
  exclusive: boolean;
  price_min: number;
  price_max: number;
  length_min: number;
  length_max: number;
  activities: string[];
  boat_types: string[];
  makes: string[];
  boat_category: string[];
  sort_by: string;
  per_page: number;
  search_mode: string;
  action: string;
  near: string;
  area: string;
  preset: string;
  max_distance: string | number;
  min_results: string | number;
  lat: string | number;
  lng: string | number;
  loc_physical_ms: string;
  loc_interest_ms: string;
  sem_account_id: string;
  page: number;
  features: ExpandedFeatures;
  experience_tags: TransformedExperienceTags;
  include_experience_tags: true;
  ne_lat?: string | number;
  ne_lng?: string | number;
  sw_lat?: string | number;
  sw_lng?: string | number;
  trip_date?: string;
  start_date?: string;
  end_date?: string;
}

export const FEATURE_EXPANSIONS: Record<string, string[]> = {
  shower: ['shower', 'deck_shower'],
  inflatable_toys: ['floating_island', 'inflatable_toys'],
};

/** Expand feature keys that map to multiple API values. */
export function expandFeatures(features: string[]): ExpandedFeatures {
  return features.flatMap((v) => FEATURE_EXPANSIONS[v] || v) as ExpandedFeatures;
}

/**
 * Strip the parent tag when sub-tags are present.
 * Atom stores [parent, ...subTags]; API expects only the sub-tags.
 */
export function transformExperienceTags(tags: string[]): TransformedExperienceTags {
  return (tags.length > 1 ? tags.slice(1) : tags) as TransformedExperienceTags;
}

/** Remove undefined and null values from a payload object. */
export function cleanPayload<T extends Record<string, any>>(payload: T): T {
  return Object.entries(payload).reduce<Record<string, any>>((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {}) as T;
}
